import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Bot, Scale, Sparkles, Zap, Mail, Save, Wand2, Trash2, CheckCircle2 } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";
import { gbp } from "../../lib/format";

const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{children}</label>;
const Input = (p) => <input className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" {...p} />;
const Stat = ({ label, value, color = "cyan" }) => (
  <div className="rounded-lg border border-white/10 p-3"><p className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{label}</p><p className={`mt-1 font-display text-[var(--neon-${color})]`}>{value}</p></div>
);

export default function AdminPricingLab() {
  const [loading, setLoading] = useState(true);
  const [comps, setComps] = useState([]);
  const [selId, setSelId] = useState("");
  const [prizeValue, setPrizeValue] = useState(50000);
  const [instantPot, setInstantPot] = useState(0);
  const [profitPct, setProfitPct] = useState(30);

  const [pricing, setPricing] = useState(null);
  const [maxRec, setMaxRec] = useState(null);
  const [question, setQuestion] = useState(null);
  const [instant, setInstant] = useState(null); // {min_revenue, prizes:[]}
  const [postal, setPostal] = useState({ user_email: "", quantity: 1 });
  const [prompt, setPrompt] = useState(""); // optional free-text steer sent to every AI tool

  const [busy, setBusy] = useState("");
  const AI = { timeout: 60000 };

  useEffect(() => {
    api.get("/competitions").then(({ data }) => setComps(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const sel = comps.find((c) => c.id === selId) || null;
  const onSelect = (id) => {
    setSelId(id);
    const c = comps.find((x) => x.id === id);
    if (c) { setPrizeValue(c.prize_value || c.prize_value || 0); }
  };
  const need = () => { if (!selId) { toast.error("Select a competition to apply this to."); return false; } return true; };

  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(getErr(e)); } finally { setBusy(""); } };

  const aiPricing = (mode) => run("pricing-" + mode, async () => {
    const { data } = await api.post("/admin/ai/recommend-pricing", { prize_value: Number(prizeValue), instant_win_total: Number(instantPot), profit_pct: Number(profitPct), mode, prompt: prompt || undefined }, AI);
    setPricing(data); toast.success("AI pricing ready.");
  });
  const applyPricing = () => need() && run("apply-pricing", async () => {
    await api.put(`/admin/competitions/${selId}`, { ticket_price: Number(pricing.ticket_price), total_tickets: Number(pricing.total_tickets) });
    toast.success("Pricing applied to competition.");
  });

  const aiMax = () => run("max", async () => {
    const { data } = await api.post("/admin/ai/recommend-max-tickets", { total_tickets: Number(pricing?.total_tickets || sel?.total_tickets || 20000), ticket_price: Number(pricing?.ticket_price || sel?.ticket_price || 4.99), prize_value: Number(prizeValue), prompt: prompt || undefined }, AI);
    setMaxRec(data); toast.success("Fairness cap recommended.");
  });
  const applyMax = () => need() && run("apply-max", async () => {
    await api.put(`/admin/competitions/${selId}`, { max_tickets_per_user: Number(maxRec.max_tickets_per_user) });
    toast.success("Fairness cap applied.");
  });

  const aiQuestion = () => run("q", async () => {
    const { data } = await api.post("/admin/ai/generate-question", { title: sel?.title || "Prize Competition", prize: sel?.prize_title || String(prizeValue), prompt: prompt || undefined }, AI);
    setQuestion(data); toast.success("Skill question generated.");
  });
  const applyQuestion = () => need() && run("apply-q", async () => {
    await api.put(`/admin/competitions/${selId}`, { skill_question: { question: question.question, options: question.options, answer_index: question.answer_index } });
    toast.success("Skill question applied.");
  });

  const aiInstant = () => run("instant", async () => {
    const { data } = await api.post("/admin/ai/recommend-instant-wins", { competition_id: selId || undefined, total_tickets: Number(pricing?.total_tickets || sel?.total_tickets || 20000), ticket_price: Number(pricing?.ticket_price || sel?.ticket_price || 4.99), prize_value: Number(prizeValue), instant_win_total: Number(instantPot), profit_pct: Number(profitPct), prompt: prompt || undefined }, AI);
    setInstant({ min_revenue: data.min_revenue, prizes: data.prizes || [] }); toast.success(`Recommended ${(data.prizes || []).length} instant-win tiers.`);
  });
  const delPrize = (i) => setInstant((s) => ({ ...s, prizes: s.prizes.filter((_, idx) => idx !== i) }));
  const saveInstant = () => need() && run("save-instant", async () => {
    await api.put(`/admin/competitions/${selId}/instant-wins`, { instant_wins_enabled: true, instant_win_min_revenue: Number(instant.min_revenue || 0), instant_win_prizes: instant.prizes });
    toast.success("Instant wins saved to competition.");
  });

  const recordPostal = () => need() && run("postal", async () => {
    if (!postal.user_email) { toast.error("Enter the entrant's email."); return; }
    await api.post(`/admin/competitions/${selId}/postal-entry`, { user_email: postal.user_email.trim(), quantity: Number(postal.quantity) || 1 });
    toast.success(`Recorded ${postal.quantity} free postal entr${postal.quantity > 1 ? "ies" : "y"}.`);
    setPostal({ user_email: "", quantity: 1 });
  });

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-pricing-lab" className="max-w-3xl">
      <div className="flex items-center gap-2"><FlaskConical className="h-6 w-6 text-[var(--neon-cyan)]" /><h2 className="font-display uppercase text-2xl text-white">AI Pricing Lab</h2></div>
      <p className="mt-1 text-sm text-[var(--text-muted)]">Real AI-powered pricing, fairness caps, skill questions, instant wins &amp; postal entries. Pick a competition to apply changes to it.</p>

      {/* Selector + inputs */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5 space-y-3">
        <div>
          <Label>Competition (to apply changes)</Label>
          <select value={selId} onChange={(e) => onSelect(e.target.value)} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="lab-comp-select">
            <option value="">— None (suggestions only) —</option>
            {comps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Prize value £</Label><Input type="number" value={prizeValue} onChange={(e) => setPrizeValue(e.target.value)} data-testid="lab-prize" /></div>
          <div><Label>Instant-win £</Label><Input type="number" value={instantPot} onChange={(e) => setInstantPot(e.target.value)} /></div>
          <div><Label>Target profit %</Label><Input type="number" value={profitPct} onChange={(e) => setProfitPct(e.target.value)} /></div>
        </div>
        <div>
          <Label>Ask the AI / steer it (optional)</Label>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} data-testid="lab-ai-prompt"
            placeholder="e.g. price for students, make it sell faster, how do I boost sales?"
            className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" />
          <p className="mt-1 text-[11px] text-[var(--text-muted)]">This steers every AI tool below and, for pricing/sales tools, gets a live data-aware answer in the reasoning.</p>
        </div>
      </NeonCard>

      {/* AI Pricing */}
      <NeonCard variant="cyan" className="mt-4" innerClassName="p-5">
        <p className="flex items-center gap-1 text-xs font-display uppercase tracking-widest glow-cyan"><Sparkles className="h-3.5 w-3.5" /> AI Ticket Pricing</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <NeonButton variant="outlinePink" onClick={() => aiPricing("cheap")} loading={busy === "pricing-cheap"} data-testid="lab-pricing-cheap"><Bot className="h-4 w-4" /> Cheap &amp; High Volume</NeonButton>
          <NeonButton variant="outlinePink" onClick={() => aiPricing("average")} loading={busy === "pricing-average"} data-testid="lab-pricing-average"><Scale className="h-4 w-4" /> Average / Balanced</NeonButton>
        </div>
        {pricing && (
          <div className="mt-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Stat label="Ticket price" value={gbp(pricing.ticket_price)} color="green" />
              <Stat label="Total tickets" value={Number(pricing.total_tickets).toLocaleString()} />
              <Stat label="Turnover" value={gbp(pricing.turnover)} color="gold" />
              <Stat label="Prize cost" value={gbp(pricing.total_prize_cost)} color="pink" />
              <Stat label="Profit" value={`${gbp(pricing.target_profit)} (${pricing.target_profit_pct}%)`} color="green" />
              <Stat label="Break-even" value={`${Number(pricing.min_tickets_to_break_even).toLocaleString()} tix`} color="gold" />
            </div>
            {pricing.reasoning && <p className="mt-3 text-xs text-[var(--text-muted)] italic">{pricing.reasoning}</p>}
            <NeonButton variant="pink" className="mt-3" onClick={applyPricing} loading={busy === "apply-pricing"} data-testid="lab-apply-pricing"><CheckCircle2 className="h-4 w-4" /> Apply to competition</NeonButton>
          </div>
        )}
      </NeonCard>

      {/* Fairness cap */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <div className="flex items-center justify-between"><p className="text-xs font-display uppercase tracking-widest glow-pink">Fairness Cap (max tickets / person)</p>
          <NeonButton variant="outlineCyan" onClick={aiMax} loading={busy === "max"} data-testid="lab-max"><Bot className="h-3.5 w-3.5" /> AI Recommend</NeonButton></div>
        {maxRec && (
          <div className="mt-3">
            <div className="flex items-center gap-3"><span className="font-display text-2xl text-[var(--neon-pink)]">{maxRec.max_tickets_per_user}</span><span className="text-xs text-[var(--text-muted)]">tickets / person ({(maxRec.share_pct)}% of pool)</span></div>
            {maxRec.reasoning && <p className="mt-2 text-xs text-[var(--text-muted)] italic">{maxRec.reasoning}</p>}
            <NeonButton variant="pink" className="mt-3" onClick={applyMax} loading={busy === "apply-max"} data-testid="lab-apply-max"><CheckCircle2 className="h-4 w-4" /> Apply cap</NeonButton>
          </div>
        )}
      </NeonCard>

      {/* AI Skill question */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <div className="flex items-center justify-between"><p className="text-xs font-display uppercase tracking-widest text-white">AI Skill Question</p>
          <NeonButton variant="outlineCyan" onClick={aiQuestion} loading={busy === "q"} data-testid="lab-question"><Wand2 className="h-3.5 w-3.5" /> Generate</NeonButton></div>
        {question && (
          <div className="mt-3">
            <p className="text-white">{question.question}</p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              {question.options.map((o, i) => <span key={i} className={`rounded-lg border px-3 py-1.5 ${i === question.answer_index ? "border-[var(--neon-green)] text-[var(--neon-green)]" : "border-white/10 text-[var(--text-muted)]"}`}>{o}</span>)}
            </div>
            <NeonButton variant="pink" className="mt-3" onClick={applyQuestion} loading={busy === "apply-q"} data-testid="lab-apply-q"><CheckCircle2 className="h-4 w-4" /> Apply question</NeonButton>
          </div>
        )}
      </NeonCard>

      {/* Instant wins */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <div className="flex items-center justify-between"><p className="flex items-center gap-1 text-xs font-display uppercase tracking-widest glow-green"><Zap className="h-3.5 w-3.5" /> Instant Wins</p>
          <NeonButton variant="outlineCyan" onClick={aiInstant} loading={busy === "instant"} data-testid="lab-instant"><Bot className="h-3.5 w-3.5" /> AI Recommend &amp; Divide</NeonButton></div>
        {instant && (
          <div className="mt-3 space-y-2">
            <p className="text-[11px] text-[var(--text-muted)]">Min revenue before drops: <span className="text-[var(--neon-gold)]">{gbp(instant.min_revenue)}</span></p>
            {instant.prizes.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_60px_30px] gap-2 items-center text-sm">
                <span className="text-white truncate">{p.label}</span>
                <span className="text-[var(--neon-green)]">{gbp(p.value)}</span>
                <span className="text-[var(--text-muted)]">x{p.quantity}</span>
                <button onClick={() => delPrize(i)} className="text-[var(--neon-pink)]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <NeonButton variant="pink" className="mt-2" onClick={saveInstant} loading={busy === "save-instant"} data-testid="lab-save-instant"><Save className="h-4 w-4" /> Save instant wins</NeonButton>
          </div>
        )}
      </NeonCard>

      {/* Postal entry */}
      <NeonCard variant="soft" className="mt-4" innerClassName="p-5">
        <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-[var(--neon-cyan)]" /><p className="font-display uppercase text-sm tracking-widest text-white">Free Postal Entry</p></div>
        <p className="mt-1 text-[11px] text-[var(--text-muted)]">Record a free postal entry (UK legal no-purchase route) against the selected competition.</p>
        <div className="mt-3 grid grid-cols-[1fr_100px] gap-2">
          <div><Label>Entrant email</Label><Input type="email" value={postal.user_email} onChange={(e) => setPostal((s) => ({ ...s, user_email: e.target.value }))} data-testid="lab-postal-email" /></div>
          <div><Label>Tickets</Label><Input type="number" min="1" value={postal.quantity} onChange={(e) => setPostal((s) => ({ ...s, quantity: e.target.value }))} data-testid="lab-postal-qty" /></div>
        </div>
        <NeonButton variant="outlinePink" className="mt-3" onClick={recordPostal} loading={busy === "postal"} data-testid="lab-postal-submit"><Mail className="h-4 w-4" /> Record postal entry</NeonButton>
      </NeonCard>
    </div>
  );
}
